import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CourseCard } from "../components/CourseCard";
import { MediaPlayer } from "../components/MediaPlayer";
import { api, FAQS } from "../lib/data";
import { ShieldCheck, Award, CalendarClock, Vote, ArrowRight, Bitcoin, TrendingUp } from "lucide-react";

const HOST_IMG = "https://static.prod-images.emergentagent.com/jobs/c4b63832-47df-4edb-af1a-ebc05239bee0/images/b43cfa13d39c4ab2a6e0b83e28160bc6abeb3cf752b911a783051e0513d27367.jpeg";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c4b63832-47df-4edb-af1a-ebc05239bee0/images/4b1e21a9a2f19f9252f2047c9fcf1c78ac5b0fa038cf159e3c4ae67f6156c647.jpeg";

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const steps = [
    { icon: TrendingUp, t: "Choose your course", d: "Pick one of six professional trading programs, from $250 to our $100,000 flagship." },
    { icon: Bitcoin, t: "Pay with Bitcoin", d: "Send payment to our secure BTC address. Once approved, you get immediate course access." },
    { icon: CalendarClock, t: "Get paid weekly", d: "Test & review the system each week. We pay you every Friday before 4pm EST for 52 weeks." },
  ];

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-200 grid-lines">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 border border-gray-300 px-3 py-1 text-xs font-mono uppercase tracking-widest text-gray-600 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00B368]" /> Trusted since 2010 · A+ BBB
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A1914] mb-6">
              Learn to trade.<br />Get <span className="text-[#00B368]">paid weekly</span> to master it.
            </h1>
            <p className="text-base text-gray-600 max-w-lg mb-8 leading-relaxed">
              Six institutional-grade trading courses — stocks, options, forex, commodities, crypto and futures.
              Buy once with Bitcoin, and earn a weekly payment for 52 weeks as you test and review the system.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/courses" data-testid="hero-courses-btn" className="inline-flex items-center gap-2 bg-[#0A1914] text-white px-6 py-3 rounded-sm hover:bg-[#00B368] transition-colors">
                View the 6 courses <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#how" className="inline-flex items-center gap-2 border border-[#0A1914] px-6 py-3 rounded-sm hover:bg-[#F3F4F3] transition-colors">
                How it works
              </a>
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
            <img src={HERO_IMG} alt="Trading chart" className="w-full border border-gray-200" />
            <div className="absolute -bottom-5 -left-5 bg-white border border-gray-200 px-5 py-3">
              <p className="font-mono text-2xl text-[#00B368]">15%+</p>
              <p className="text-xs text-gray-500">avg ROI reported by traders*</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Award, t: "A+ BBB Rated", d: "20+ years trusted" },
            { icon: ShieldCheck, t: "Est. 2010", d: "Operating over a decade" },
            { icon: Vote, t: "Member Governed", d: "Owners are the CEO" },
            { icon: CalendarClock, t: "Paid Fridays", d: "Weekly for 52 weeks" },
          ].map((x) => (
            <div key={x.t} className="flex items-center gap-3">
              <x.icon className="h-6 w-6 text-[#00B368]" />
              <div>
                <p className="text-sm font-semibold text-[#0A1914]">{x.t}</p>
                <p className="text-xs text-gray-500">{x.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Courses */}
      <section className="max-w-7xl mx-auto px-6 py-20" id="courses">
        <div className="mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-[#00B368] mb-3">The Offering</p>
          <h2 className="font-display text-4xl tracking-tight text-[#0A1914] mb-3">Six tiers. Six markets. One path to mastery.</h2>
          <p className="text-gray-600 max-w-2xl">Each course grants immediate video access on approval, plus a weekly payment for 52 weeks to test and review what you learn.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c, i) => <CourseCard key={c.id} course={c} index={i} />)}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-gray-200 bg-white" id="how">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#00B368] mb-3">Watch & Listen</p>
            <h2 className="font-display text-4xl tracking-tight text-[#0A1914] mb-6">How the program works</h2>
            <div className="space-y-6">
              {steps.map((s, i) => (
                <div key={s.t} className="flex gap-4">
                  <div className="shrink-0 h-10 w-10 border border-[#0A1914] flex items-center justify-center font-mono text-sm">{i + 1}</div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon className="h-4 w-4 text-[#00B368]" />
                      <p className="font-semibold text-[#0A1914]">{s.t}</p>
                    </div>
                    <p className="text-sm text-gray-600">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <MediaPlayer kind="explainer" itemId="explainer" image={HOST_IMG} label="How Electronics-Galore.com Works" role="Program Overview · Press play" caption={"Choose a course, pay with Bitcoin, get instant access, then test & review it weekly to get paid every Friday for 52 weeks."} />
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-[#00B368] mb-3">Member Stories</p>
          <h2 className="font-display text-4xl tracking-tight text-[#0A1914] mb-3">Real members. Real video testimonials.</h2>
          <p className="text-gray-600 max-w-2xl">Press play to see and hear what our members say about the courses and weekly payouts.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((t) => (
            <MediaPlayer key={t.id} kind="testimonial" itemId={t.id} image={t.image} name={t.name} role={t.role} caption={t.script} />
          ))}
        </div>
      </section>

      {/* FAQ preview */}
      <section className="border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <p className="font-mono text-xs uppercase tracking-widest text-[#00B368] mb-3">Questions</p>
          <h2 className="font-display text-4xl tracking-tight text-[#0A1914] mb-8">Frequently asked</h2>
          <div className="divide-y divide-gray-200 border-y border-gray-200">
            {FAQS.slice(0, 4).map((f) => (
              <div key={f.q} className="py-5">
                <p className="font-semibold text-[#0A1914] mb-1">{f.q}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <Link to="/faq" data-testid="see-all-faq" className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-[#00B368] hover:text-[#0A1914] transition-colors">
            See all FAQs <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
